// =============================================================
// OpportunityDocsModal.jsx — tracked documents for a Gov Opportunity
//
// Lists documents tracked against a GovWin opportunity (auto-pulled from
// SAM.gov, pulled from GovWin IQ itself, manually attached, or the GovWin IQ
// source link), and lets an admin pull from SAM.gov, pull everything GovWin
// holds on a GovWin IQ bid (details, contacts, related documents…), or attach
// files. Used by GovWins.jsx.
// =============================================================
import { useState, useEffect, useCallback, useRef } from 'react';
import { B } from '../../constants/brand';
import { fmt } from '../../utils/format';
import { api } from '../../api/client';
import FileDropZone from './FileDropZone';
import { Btn, Modal, Spinner, Badge, Icon, useToast } from '../ui';

const SOURCE_LABEL = { samgov: 'SAM.gov', govwin: 'GovWin IQ', manual: 'Uploaded', link: 'Source link' };
const SOURCE_COLOR = { samgov: B.navy, govwin: B.tealDk, manual: B.tealDk, link: B.textMd };
// The GovWin pull reads seven resources and downloads the related documents
// inline — well past the client's 10 s default.
const GOVWIN_PULL_TIMEOUT_MS = 3 * 60 * 1000;

const prettySize = (n) => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

export const OpportunityDocsModal = ({ opportunity, isAdmin = false, onClose, onChanged }) => {
  const { toast } = useToast();
  const opp   = opportunity || {};
  const oppId = opp.opportunityId;

  const [docs, setDocs]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [pulling, setPulling] = useState(false);
  const [pullingIq, setPullingIq] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fromGovWinIq = opp.sourceSystem === 'GovWin IQ';
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!oppId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(`govwins/${oppId}/documents`);
      setDocs(data.documents || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [oppId]);

  useEffect(() => { load(); }, [load]);

  const pull = async () => {
    setPulling(true);
    try {
      const res = await api.post(`govwins/${oppId}/documents/pull`, {});
      setDocs(res.documents || []);
      onChanged?.();
      if (res.error) toast?.error?.(`SAM.gov pull failed: ${res.error}`);
      else if (res.pulled) toast?.success?.(`Pulled ${res.pulled} document(s) from SAM.gov`);
      else toast?.info?.(res.reason || 'No new SAM.gov documents found');
    } catch (e) {
      toast?.error?.(`Pull failed: ${e.message}`);
    } finally {
      setPulling(false);
    }
  };

  // Everything GovWin IQ holds on this bid: details, contacts, related
  // documents, notices, contracts, milestones, places of performance. The
  // server stamps the row and lands the documents; the answer carries the
  // refreshed list. A refusal is shown verbatim.
  const pullIq = async () => {
    setPullingIq(true);
    try {
      const res = await api.post(`govwins/${oppId}/iq/enrich`, {}, { timeout: GOVWIN_PULL_TIMEOUT_MS });
      if (res.documents) setDocs(res.documents);
      onChanged?.();
      if (res.ok === false) toast?.error?.(res.error || 'GovWin IQ refused the pull');
      else if (res.status === 'no-api-id') toast?.info?.(res.error || 'This row carries no GovWin API id yet — the next sync refreshes it.');
      else {
        const d = res.docs || {};
        toast?.success?.(`Pulled from GovWin IQ: ${res.calls} resource reads${d.pulled ? `, ${d.pulled} document(s) landed` : ''}${d.recorded ? `, ${d.recorded} recorded without a file` : ''}${res.error ? ` · ${res.error}` : ''}`);
      }
    } catch (e) {
      toast?.error?.(`GovWin IQ pull failed: ${e.message}`);
    } finally {
      setPullingIq(false);
    }
  };

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const { documentId, sasUrl } = await api.post(`govwins/${oppId}/documents/upload-url`, {
          fileName: file.name, fileType: file.type || 'application/octet-stream',
        });
        const putRes = await fetch(sasUrl, {
          method: 'PUT',
          headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!putRes.ok) throw new Error(`Upload failed (${putRes.status}) for ${file.name}`);
        await api.post(`govwins/${oppId}/documents/${documentId}/confirm-upload`, { fileSizeBytes: file.size });
      }
      await load();
      onChanged?.();
      toast?.success?.(`Attached ${files.length} file(s)`);
    } catch (e) {
      toast?.error?.(e.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const download = async (doc) => {
    try {
      const { url } = await api.get(`govwins/${oppId}/documents/${doc.documentId}/download-url`);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast?.error?.(`Could not open: ${e.message}`);
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Remove "${doc.fileName}"?`)) return;
    try {
      await api.del(`govwins/${oppId}/documents/${doc.documentId}`);
      setDocs(d => (d || []).filter(x => x.documentId !== doc.documentId));
      onChanged?.();
    } catch (e) {
      toast?.error?.(`Delete failed: ${e.message}`);
    }
  };

  return (
    <Modal title="Documents" onClose={onClose} width={640}>
      <FileDropZone onFiles={onFiles} disabled={!isAdmin || uploading} label="Drop to attach to this bid">
      <div style={{ marginBottom: 4, fontWeight: 700, fontSize: 15, color: B.text }}>{opp.title}</div>
      <div style={{ fontSize: 12, color: B.textLt, marginBottom: 16 }}>
        {opp.solicitationNumber
          ? <>Solicitation #{opp.solicitationNumber}</>
          : <>No solicitation number — SAM.gov auto-pull unavailable; attach files manually.</>}
      </div>

      {isAdmin && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <Btn size="sm" onClick={pull} disabled={pulling || !opp.solicitationNumber}
            title={opp.solicitationNumber ? 'Fetch attachments from SAM.gov' : 'Requires a solicitation number'}>
            {pulling ? 'Pulling…' : <><Icon name="download" size={14} /> Pull from SAM.gov</>}
          </Btn>
          {fromGovWinIq && (
            <Btn size="sm" variant="secondary" onClick={pullIq} disabled={pullingIq}
              title="Read everything GovWin IQ holds on this bid — details, contacts, related documents, notices, contracts, milestones, places of performance">
              {pullingIq ? 'Pulling from GovWin IQ…' : <><Icon name="download" size={14} /> Pull from GovWin IQ</>}
            </Btn>
          )}
          <Btn size="sm" variant="ghost" onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? 'Uploading…' : <><Icon name="upload" size={14} /> Attach files (or drop them here)</>}
          </Btn>
          <input ref={fileRef} type="file" multiple style={{ display: 'none' }}
            onChange={e => onFiles(e.target.files)} />
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: 30 }}><Spinner /></div>}
      {error && <div style={{ color: B.red, fontSize: 13, padding: '8px 0' }}>{error}</div>}

      {!loading && docs && docs.length === 0 && (
        <div style={{ fontSize: 13, color: B.textLt, padding: '16px 0' }}>
          No documents tracked yet.{isAdmin && fromGovWinIq ? ' Try “Pull from GovWin IQ”.' : isAdmin && opp.solicitationNumber ? ' Try “Pull from SAM.gov”.' : ''}
        </div>
      )}

      {!loading && docs && docs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {docs.map(doc => (
            <div key={doc.documentId} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 10px', background: B.surfaceMuted, borderRadius: 6, fontSize: 13,
            }}>
              <Icon name={doc.source === 'link' ? 'external' : 'file'} size={15} style={{ color: B.textMd, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: B.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {doc.fileName}
                </div>
                <div style={{ fontSize: 11, color: B.textLt }}>
                  {prettySize(doc.sizeBytes)}
                  {doc.createdUTC && ` · ${fmt.date(doc.createdUTC)}`}
                  {doc.pullError && <span style={{ color: doc.source === 'govwin' && !doc.hasBlob ? B.amber : B.red }}> · {doc.pullError}</span>}
                </div>
              </div>
              <Badge color={SOURCE_COLOR[doc.source] || B.textMd} style={{ fontSize: 10 }}>
                {SOURCE_LABEL[doc.source] || doc.source}
              </Badge>
              {(doc.hasBlob || doc.sourceUrl) && (
                <Btn size="sm" variant="ghost" onClick={() => download(doc)} style={{ padding: '2px 8px' }}>
                  {doc.hasBlob ? 'Download' : 'Open'}
                </Btn>
              )}
              {isAdmin && (
                <Btn size="sm" variant="ghost" onClick={() => remove(doc)} style={{ color: B.red, padding: '2px 8px' }}>
                  Remove
                </Btn>
              )}
            </div>
          ))}
        </div>
      )}
      </FileDropZone>
    </Modal>
  );
};

export default OpportunityDocsModal;
